import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import type Guid from "_shared/guids/guid";
import { useEffect, useState } from "react";
import type Session from "users/__session/session";
import { useSession } from "users/__session/session-context";
import { markCommentsRead } from "work/proposals/comments/_store/comments-redux-slice";

/** What a comment row tells the pane about itself. See `CommentItem`'s `readWhenSeen`. */
export type SeenCommentWatcher = {
  /** Starts watching one comment's row as it mounts, and answers with what stops the watching. */
  watch: (row: HTMLElement, commentId: string) => () => void;
  /**
   * The reader marked the comment unread by hand. It is not read again while its row stays in view:
   * only once the row has left the screen and come back.
   */
  holdUnread: (commentId: string | undefined) => void;
};

/** How long a row stays on screen before it counts as read: a row a scroll only passes over is not. */
export const commentSeenDwellMs = 300;

/** The share of a row on screen that counts as seeing it. */
const seenRatio = 0.5;
/** A row taller than twice this is never half on screen in a short list, so this much of it counts. */
export const seenHeightPx = 120;
/** An observer reports a ratio a hair under the threshold that woke it. */
const ratioTolerance = 0.001;

/** The share of a row of this height that has to be on screen: half of it, or `seenHeightPx` of a tall one. */
export function seenThreshold(height: number): number {
  return height > 0 ? Math.min(seenRatio, seenHeightPx / height) : seenRatio;
}

type WatchedRow = {
  row: HTMLElement;
  observer: IntersectionObserver;
  threshold: number;
  resize?: ResizeObserver;
  inView: boolean;
  /** The reader marked it unread while it was on screen. */
  held: boolean;
  dwell?: ReturnType<typeof setTimeout>;
};

type PaneState = { session: Session | undefined; proposalId: Guid | undefined; isOpen: boolean };

/**
 * Which comment rows the reader has seen, and the read receipts that follows from it. Outside React so
 * that the rows, their timers and the writes are plain state a test can drive, and so that the React
 * Compiler has none of it to lower.
 *
 * - Each row has its own observer, woken at the share of it that counts as seen. That share comes from
 *   the row's height, and is taken again whenever the row changes size.
 * - Each row that comes on screen waits `commentSeenDwellMs` on its own clock. Another row arriving
 *   does not move it.
 * - Nothing is read while the pane is shut or the browser tab is hidden. Hiding cancels every wait;
 *   showing starts each on-screen row's wait again.
 * - One write at a time. Each answer carries the forum's unread count after its write, and the count
 *   the store keeps is the last answer's, so two writes in flight could leave the older count standing.
 *   Rows that come due during a write go in the next one.
 */
export class SeenCommentReader {
  readonly watcher: SeenCommentWatcher = {
    watch: (row, commentId) => this.watch(row, commentId),
    holdUnread: (commentId) => this.holdUnread(commentId),
  };

  constructor(
    private readonly write: (session: Session | undefined, proposalId: Guid, commentIds: string[]) => Promise<void>,
    private readonly document: Document,
  ) {}

  setPane(pane: PaneState): void {
    const wasReading = this.canRead();
    this.pane = pane;
    if (this.canRead() && !wasReading) this.restartEveryDwell();
    if (!this.canRead()) this.cancelEveryDwell();
  }

  /** The browser tab was hidden or shown. */
  visibilityChanged(): void {
    if (this.canRead()) this.restartEveryDwell();
    else this.cancelEveryDwell();
  }

  /** The pane is going: no timer outlives it. Each row stops its own observer as it unmounts. */
  stop(): void {
    this.cancelEveryDwell();
    if (this.flushTimer !== undefined) clearTimeout(this.flushTimer);
    this.flushTimer = undefined;
  }

  private readonly rows = new Map<string, WatchedRow>();
  private readonly due = new Set<string>();
  private pane: PaneState = { session: undefined, proposalId: undefined, isOpen: false };
  private writing = false;
  private flushTimer: ReturnType<typeof setTimeout> | undefined;

  private watch(row: HTMLElement, commentId: string): () => void {
    const threshold = seenThreshold(row.getBoundingClientRect().height);
    const watched: WatchedRow = {
      row,
      threshold,
      observer: this.observe(row, commentId, threshold),
      inView: false,
      held: false,
    };
    if (typeof ResizeObserver !== "undefined") {
      watched.resize = new ResizeObserver(() => this.rowResized(commentId));
      watched.resize.observe(row);
    }
    this.rows.set(commentId, watched);
    return () => {
      watched.observer.disconnect();
      watched.resize?.disconnect();
      clearTimeout(watched.dwell);
      this.due.delete(commentId);
      if (this.rows.get(commentId) === watched) this.rows.delete(commentId);
    };
  }

  private observe(row: HTMLElement, commentId: string, threshold: number): IntersectionObserver {
    const observer = new IntersectionObserver((entries) => entries.forEach((entry) => this.reported(commentId, entry)), {
      threshold: [0, threshold],
    });
    observer.observe(row);
    return observer;
  }

  /** A row's height changed, and with it the share of it that counts as seen. */
  private rowResized(commentId: string): void {
    const watched = this.rows.get(commentId);
    if (!watched) return;
    const threshold = seenThreshold(watched.row.getBoundingClientRect().height);
    if (Math.abs(threshold - watched.threshold) < ratioTolerance) return;
    watched.observer.disconnect();
    watched.threshold = threshold;
    watched.observer = this.observe(watched.row, commentId, threshold);
  }

  private reported(commentId: string, entry: IntersectionObserverEntry): void {
    const watched = this.rows.get(commentId);
    if (!watched) return;
    const seen = entry.isIntersecting && entry.intersectionRatio >= watched.threshold - ratioTolerance;
    if (seen === watched.inView) return;
    watched.inView = seen;
    if (seen) {
      this.startDwell(commentId, watched);
      return;
    }
    // Off screen: whatever was waiting is cancelled, and a comment held unread can be read again once
    // it comes back.
    clearTimeout(watched.dwell);
    watched.dwell = undefined;
    watched.held = false;
    this.due.delete(commentId);
  }

  private holdUnread(commentId: string | undefined): void {
    if (!commentId) return;
    const watched = this.rows.get(commentId);
    if (!watched) return;
    watched.held = true;
    clearTimeout(watched.dwell);
    watched.dwell = undefined;
    this.due.delete(commentId);
  }

  private canRead(): boolean {
    return this.pane.isOpen && Boolean(this.pane.proposalId) && this.document.visibilityState === "visible";
  }

  private startDwell(commentId: string, watched: WatchedRow): void {
    if (!this.canRead() || watched.held || watched.dwell !== undefined) return;
    // Whatever would stop the read — the row leaving, a hold, the pane or the tab going — cancels this
    // timer, so a timer that fires has nothing left to ask.
    watched.dwell = setTimeout(() => {
      watched.dwell = undefined;
      this.due.add(commentId);
      this.scheduleFlush();
    }, commentSeenDwellMs);
  }

  private restartEveryDwell(): void {
    this.rows.forEach((watched, commentId) => {
      if (watched.inView) this.startDwell(commentId, watched);
    });
  }

  private cancelEveryDwell(): void {
    this.rows.forEach((watched) => {
      clearTimeout(watched.dwell);
      watched.dwell = undefined;
    });
    this.due.clear();
  }

  /** Rows that come due in the same moment go up together. */
  private scheduleFlush(): void {
    if (this.flushTimer !== undefined) return;
    this.flushTimer = setTimeout(() => this.flush(), 0);
  }

  private flush(): void {
    this.flushTimer = undefined;
    const { session, proposalId } = this.pane;
    if (this.writing || !proposalId) return;
    const commentIds = [...this.due];
    this.due.clear();
    if (commentIds.length === 0) return;
    this.writing = true;
    void this.write(session, proposalId, commentIds).then(() => {
      this.writing = false;
      if (this.due.size > 0) this.scheduleFlush();
    });
  }
}

/**
 * Marks a proposal's comments read as the reader scrolls them into view in the Comments pane (#5824).
 *
 * The pane lists one conversation at a time, so a comment that comes into view is on the tab the reader
 * is on: the other tab's comments stay unread until the reader opens that tab and sees them. Nothing
 * else marks a comment read but its own row's Mark read and the open tab's Mark all read. Opening the
 * pane, a traverser stop and the composer used to sweep both conversations read at once.
 *
 * The rules are `SeenCommentReader`'s. The observers' root is the viewport, which clips by every
 * scrolling ancestor, so a row scrolled out of the list, or in a drawer that slid shut, is not on
 * screen. Answers with no watcher where the browser has no `IntersectionObserver`.
 */
export default function useReadCommentsWhenSeen(args: {
  /** The negotiation the comments are filed under. */
  proposalId: Guid | undefined;
  /** The pane is on screen. A pane kept mounted behind a closed drawer marks nothing. */
  isOpen: boolean;
}): SeenCommentWatcher | undefined {
  const { proposalId, isOpen } = args;
  const session = useSession();
  const dispatch = useAttorneyHubDispatch();
  const [reader] = useState(() =>
    typeof IntersectionObserver === "undefined"
      ? undefined
      : new SeenCommentReader(
          (reading, conversationId, commentIds) => writeReadReceipts(dispatch, reading, conversationId, commentIds),
          globalThis.document,
        ),
  );

  useEffect(() => {
    reader?.setPane({ session, proposalId, isOpen });
  }, [isOpen, proposalId, reader, session]);

  useEffect(() => {
    if (!reader) return;
    const visibilityChanged = () => reader.visibilityChanged();
    globalThis.document.addEventListener("visibilitychange", visibilityChanged);
    return () => {
      globalThis.document.removeEventListener("visibilitychange", visibilityChanged);
      reader.stop();
    };
  }, [reader]);

  return reader?.watcher;
}

/**
 * Writes the read receipts. Outside the hook because it needs a `catch`, and the React Compiler skips
 * code it cannot lower. A failure is logged and not announced: the comment stays unread, and is written
 * again the next time its row comes into view.
 */
async function writeReadReceipts(
  dispatch: ReturnType<typeof useAttorneyHubDispatch>,
  session: Session | undefined,
  proposalId: Guid,
  commentIds: readonly string[],
): Promise<void> {
  if (!session) return;
  try {
    await dispatch(markCommentsRead({ session, proposalId, commentIds })).unwrap();
  } catch (error) {
    console.error("Failed to mark the comments in view read", error);
  }
}
